import {
  Avatar,
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { format } from 'date-fns';
import { useSnackbar } from 'notistack';
import { ReactNode, useCallback, useState } from 'react';
import { visitApi } from 'src/api/visit';
import Image from 'src/components/image';
import Scrollbar from 'src/components/scrollbar';
import VisitStatusBadge from 'src/components/visit-status-badge/VisitStatusBadge';
import VisitTypeBadge from 'src/components/visit-type-badge/VisitTypeBadge';
import OrganizationCancelVisitModal from 'src/sections/@dashboard/organization/components/OrganizationCancelVisitModal';
import OrganizationVisitModal from 'src/sections/@dashboard/organization/components/OrganizationVisitModal/OrganizationVisitModal';
import {
  GetProfessionalConfigDtoEnabledCategoriesEnum,
  GetVisitById,
  GetVisitByIdRejectedByEnum,
  GetVisitByIdStatusEnum,
  GetVisitByIdVisitTypeEnum,
  VisitApiVisitControllerCancelVisitByStaffRequest,
  VisitApiVisitControllerRescheduleVisitByStaffRequest,
} from 'woofwoof-api';
import { translateKey } from 'src/locales/translate';


interface Row {
  id: string;
  profile?: string;
  profileAvatar?: string;
  date: string;
  user?: string;
  userAvatar?: string;
  professional?: string;
  professionalAvatar?: string;
  category: GetProfessionalConfigDtoEnabledCategoriesEnum;
  visitType: GetVisitByIdVisitTypeEnum;
  status: GetVisitByIdStatusEnum;
  rejectedBy?: GetVisitByIdRejectedByEnum;
}

interface Column {
  id: keyof Row;
  label: string;
  minWidth?: number;
  align?: 'left';
  render?: (row: Row) => ReactNode;
}

interface OrganizationProfessionalWaitingRoomTableProps {
  visits: GetVisitById[];
  nextVisit?: GetVisitById;
  refetch: () => void;
}

export default function OrganizationProfessionalWaitingRoomTable({
  visits,
  nextVisit,
  refetch,
}: OrganizationProfessionalWaitingRoomTableProps) {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const [selectedVisitId, setSelectedVisitId] = useState('');
  const [isRescheduleLoading, setIsRescheduleLoading] = useState(false);
  const [isCancelLoading, setIsCancelLoading] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);

  const { mutateAsync: rescheduleVisitByStaff } = useMutation({
    mutationFn: (
      rescheduleVisitByStaffDto: VisitApiVisitControllerRescheduleVisitByStaffRequest,
    ) =>
      visitApi.visitControllerRescheduleVisitByStaff(rescheduleVisitByStaffDto),
  });

  const { mutateAsync: cancelVisitByStaff } = useMutation({
    mutationFn: (
      cancelVisitByStaffDto: VisitApiVisitControllerCancelVisitByStaffRequest,
    ) => visitApi.visitControllerCancelVisitByStaff(cancelVisitByStaffDto),
  });

  const openModal = (visitId: string) => setSelectedVisitId(visitId);

  const closeModal = () => setSelectedVisitId('');

  const onOpenCancelModal = () => setIsCancelModalOpen(true);

  const onCloseCancelModal = useCallback(() => setIsCancelModalOpen(false), []);

  const onReschedule = useCallback(
    async ({
      professionalId,
      date,
      visitId,
    }: {
      professionalId: string;
      date: string;
      visitId: string;
    }) => {
      try {
        setIsRescheduleLoading(true);
        await rescheduleVisitByStaff({
          visitId,
          rescheduleStaffVisitDto: {
            newStartTime: date,
            newProfessionalId: professionalId,
          },
        });
        await refetch();
        closeModal();
        enqueueSnackbar(translateKey("snackbar.visitUpdated"), {
          variant: 'success',
        });
      } catch (error) {
        console.error(error);
      } finally {
        setIsRescheduleLoading(false);
      }
    },
    [rescheduleVisitByStaff, enqueueSnackbar, refetch],
  );

  const onCancel = useCallback(async (reason?: string) => {
    try {
      setIsCancelLoading(true);
      await cancelVisitByStaff({
        visitId: selectedVisitId,
        cancelStaffVisitDto: {
          cancellationReason: reason || translateKey("visit.defaultCancelReasonByStaff"),
        },
      });
      await refetch();
      onCloseCancelModal();
      closeModal();
      enqueueSnackbar(translateKey("snackbar.visitCancelled"), {
        variant: 'success',
      });
    } catch (error) {
      console.error(error);
    } finally {
      setIsCancelLoading(false);
    }
  }, [
    onCloseCancelModal,
    setIsCancelLoading,
    cancelVisitByStaff,
    enqueueSnackbar,
    refetch,
    selectedVisitId,
  ]);

  const handleOnRowClick = (visitId: string) => {
    openModal(visitId);
  };

  const tableData: Row[] | [] =
    visits?.map((visit: GetVisitById) => ({
      id: visit.id,
      profile: visit?.profile?.name,
      profileAvatar: visit?.profile?.avatar,
      user: `${visit?.user?.firstName}  ${visit?.user?.lastName}`,
      userAvatar: visit?.user?.avatar,
      professional: `${visit?.professional?.firstName}  ${visit?.professional?.lastName}`,
      professionalAvatar: visit?.professional?.avatar,
      date: visit.startTime,
      visitType: visit.visitType,
      status: visit.status,
      rejectedBy: visit.rejectedBy,
      category: visit.category,
    })) || [];

  const COLUMNS: Column[] = [
    {
      id: 'profile',
      // Staff-facing, like the clinic-wide waiting room it mirrors.
      label: translateKey("visit.tablePatient"),
      render: (value: Row) => (
        <Box
          sx={{
            gap: theme.spacing(2),
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Avatar
            src={value.profileAvatar || '/assets/avatars/avatar4.png'}
            sx={{ width: theme.spacing(5), height: theme.spacing(5) }}
          />
          <Typography variant="body2">{value.profile}</Typography>
        </Box>
      ),
    },
    {
      id: 'user',
      label: translateKey("common.owner"),
      render: (value: Row) => (
        <Box
          sx={{
            gap: theme.spacing(2),
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Avatar
            src={value.userAvatar || '/assets/avatars/avatar.png'}
            sx={{ width: theme.spacing(5), height: theme.spacing(5) }}
          />
          <Typography variant="body2">{value.user}</Typography>
        </Box>
      ),
    },
    {
      id: 'date',
      align: 'center' as any,
      label: translateKey("visit.tableTime"),
      render: (value: Row) => format(new Date(value.date), 'H:mm'),
    },
    {
      id: 'visitType',
      label: translateKey("visit.tableKind"),
      render: (value: Row) => <VisitTypeBadge visitType={value.visitType} />,
    },
    {
      id: 'status',
      label: translateKey("visit.tableStatus"),
      render: (value: Row) => (
        <VisitStatusBadge
          visitStatus={value.status}
          rejectedBy={value.rejectedBy}
        />
      ),
    },
  ];

  const TableHeader = (
    <Table stickyHeader>
      <TableHead>
        <TableRow>
          {COLUMNS.map((column) => (
            <TableCell
              key={column.id}
              align={column.align}
              style={{ minWidth: column.minWidth }}
            >
              {column.label}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
    </Table>
  );

  const FullTable = (
    <Box sx={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0 }}>
      <Scrollbar>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {COLUMNS.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align}
                  style={{ minWidth: column.minWidth }}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {tableData?.map((row, rowIndex) => (
              <TableRow
                hover
                key={rowIndex}
                data-testid="waiting-room-row"
                data-visit-id={row.id}
                onClick={() => handleOnRowClick(row.id)}
              >
                {COLUMNS.map((column, index) => {
                  const value = row[column.id];
                  return (
                    <TableCell key={column.id} align={column.align}>
                      {column.render ? column.render(row) : value}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Scrollbar>
    </Box>
  );

  return (
    <>
      <TableContainer
        sx={{ height: 1, display: 'flex', flexDirection: 'column' }}
      >
        {tableData.length > 0 ? (
          FullTable
        ) : (
          <>
            {TableHeader}
            <Stack
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              flex="1"
              gap={2}
            >
              <Image
                sx={{ width: '200px' }}
                alt="empty waiting room"
                src="/assets/images/empty_table.svg"
              />
              <Typography>
                {nextVisit ? translateKey("visit.upcomingNoMoreVisits") : translateKey("visit.upcomingNoVisits")}
              </Typography>
            </Stack>
          </>
        )}
      </TableContainer>
      <OrganizationVisitModal
        isOpen={Boolean(selectedVisitId)}
        onClose={closeModal}
        visit={visits?.find((visit) => visit.id === selectedVisitId)}
        onReschedule={onReschedule}
        isRescheduleLoading={isRescheduleLoading}
        onCancel={onOpenCancelModal}
        isCancelLoading={isCancelLoading}
      />
      <OrganizationCancelVisitModal
        isOpen={isCancelModalOpen}
        onClose={onCloseCancelModal}
        onAccept={onCancel}
        isLoading={isCancelLoading}
      />
    </>
  );
}
